import { isAxiosError } from 'axios';

import { DealsDashboardClient } from '@/shared/api';
import { apiUri } from '@/constants/Constants';
import {
  type SubmitLeaseDetailsParams,
  type SubmitLeaseDetailsResponse,
} from './lease-details.types';

/**
 * A failed lease-details submission. `message` stays the transport message for monitoring;
 * `userMessage` is set only when the API rejected the submission in words the landlord can act on.
 */
export class LeaseDetailsSubmitError extends Error {
  readonly userMessage?: string;

  constructor(message: string, options?: { userMessage?: string; cause?: unknown }) {
    super(message, { cause: options?.cause });
    this.name = 'LeaseDetailsSubmitError';
    this.userMessage = options?.userMessage;
  }
}

// 409: a submission is already in progress or pending review; 400: renter mismatch or invalid
// fields. Other statuses carry internals (5xx) or unhelpful text (404 "Deal not found"); 401 and
// 403 never get here because the client logs the user out.
const PRESENTABLE_STATUSES = new Set([400, 409]);

const toSubmitError = (error: unknown): LeaseDetailsSubmitError => {
  if (!isAxiosError<{ message?: unknown }>(error)) {
    return new LeaseDetailsSubmitError(
      error instanceof Error ? error.message : 'Failed to submit lease details',
      { cause: error },
    );
  }
  const status = error.response?.status ?? 0;
  const apiMessage = error.response?.data?.message;
  return new LeaseDetailsSubmitError(error.message, {
    userMessage:
      PRESENTABLE_STATUSES.has(status) && typeof apiMessage === 'string' ? apiMessage : undefined,
    cause: error,
  });
};

export const submitLeaseDetails = async (leaseData: SubmitLeaseDetailsParams) => {
  let response;
  try {
    response = await DealsDashboardClient.post<SubmitLeaseDetailsResponse>(
      apiUri.submitLeaseDetails,
      leaseData,
    );
  } catch (error) {
    throw toSubmitError(error);
  }

  const { status, data } = response;
  if (status >= 300 || data.status_code >= 300 || !data?.data) {
    throw new LeaseDetailsSubmitError('Failed to submit lease details');
  }

  return data.data;
};
